import type { RichDoc } from './doc';

// ------------------------------------------------------------------ announcements (#22)
export type AnnouncementPriority = 'normal' | 'important' | 'urgent';
export const ANNOUNCEMENT_PRIORITIES: { key: AnnouncementPriority; label: string }[] = [
  { key: 'normal', label: 'Normal' }, { key: 'important', label: 'Important' }, { key: 'urgent', label: 'Urgent' },
];
export type AnnouncementState = 'scheduled' | 'live' | 'expired';
export interface Audience { location_ids: string[]; team_ids: string[] }
/** t140: 'both' = only people in one of the buildings and also in one of the teams; 'any' = in any of them. */
export type AudienceMatch = 'any' | 'both';
/**
 * t521u: announcements can also be aimed at positions: anyone holding the position (any of their positions, not only
 * the primary). With 'both', positions count on the same side as teams: in one of the buildings AND in one of the
 * picked teams or positions.
 */
export interface AnnouncementAudienceRefs extends Audience { position_ids?: string[] }
export interface Announcement extends Audience {
  /** t521u: positions in the audience (absent from older API responses). */
  position_ids?: string[];
  id: string; title: string; priority: AnnouncementPriority; body_html: string; excerpt: string; cover_id: string | null;
  pinned: boolean; require_ack: boolean; ack_due_at: string | null; publish_at: string; expires_at: string | null; state: AnnouncementState;
  version: number; author_id: string; author_name: string; created_at: string; updated_at: string;
  audience_label: string; audience_match: AudienceMatch; read_at: string | null; acked_version: number | null; can_edit: boolean;
  /** require_ack, not yet acknowledged at this version, and not the author. */
  ack_required: boolean;
  /** Only for editors / stats holders. */
  stats: { audience: number; read: number; acked: number } | null;
  /** t172u: the first image in the body for card previews (a signed URL, valid ~1 hour), and how many images there are. */
  preview_image?: AnnouncementPreviewImage | null;
  image_count?: number;
  /** t522u: who may reply / react ('everyone' when absent - older API). Principals only for editors. */
  reply_policy?: InteractionPolicy; react_policy?: InteractionPolicy;
  reply_principals?: AnnouncementPrincipal[]; react_principals?: AnnouncementPrincipal[];
  /** t522u: may the caller reply / react (the author, owners and admins always can). Absent = yes (older API). */
  can_reply?: boolean; can_react?: boolean;
}
/** t522u: 'everyone' = everyone who can see it; 'selected' = only the listed principals; 'none' = no one. */
export type InteractionPolicy = 'everyone' | 'selected' | 'none';
/** Same shape as channel permission principals (t520u), plus positions. name is filled in for editors. */
export type AnnouncementPrincipalType = 'everyone' | 'user' | 'team' | 'location' | 'position';
export interface AnnouncementPrincipal { type: AnnouncementPrincipalType; id?: string | null; name?: string | null }
export interface AnnouncementInteractions {
  reply_policy?: InteractionPolicy; reply_principals?: AnnouncementPrincipal[];
  react_policy?: InteractionPolicy; react_principals?: AnnouncementPrincipal[];
}
export interface AnnouncementPreviewImage { url: string; alt: string; width: number | null; height: number | null }
export interface AnnouncementDetail extends Announcement { body: RichDoc }
export interface AnnouncementInput extends Partial<Audience> {
  position_ids?: string[];
  title: string; body: RichDoc; priority?: AnnouncementPriority; cover_id?: string | null; pinned?: boolean; require_ack?: boolean;
  ack_due_at?: string | null; publish_at?: string | null; expires_at?: string | null; reack?: boolean; audience_match?: AudienceMatch;
  reply_policy?: InteractionPolicy; reply_principals?: AnnouncementPrincipal[]; react_policy?: InteractionPolicy; react_principals?: AnnouncementPrincipal[];
}
export interface AnnouncementStatsRow { user_id: string; display_name: string; read_at: string | null; acked_at: string | null; acked_version: number | null }

// ------------------------------------------------------------------ knowledge base (#23)
export type ArticleStatus = 'draft' | 'in_review' | 'published' | 'archived';
export interface KbCollection extends Audience {
  id: string; parent_id: string | null; name: string; description: string | null; icon: string | null; sort_order: number; article_count: number;
}
export interface KbArticleSummary extends Audience {
  id: string; collection_id: string | null; collection_name: string | null; title: string; summary: string | null; tags: string[];
  status: ArticleStatus; is_template: boolean; is_policy: boolean; published_version: number | null; draft_version: number;
  owner_id: string; owner_name: string; updated_at: string; published_at: string | null; helpful: number; not_helpful: number;
  /** Policies: whether the caller must (re)acknowledge the published version. */
  ack_required: boolean; acked_version: number | null; snippet?: string | null;
}
export interface KbArticle extends KbArticleSummary {
  /** Published content for readers, or the draft for editors (?draft=1). */
  body: RichDoc; body_html: string; headings: { level: number; text: string; id: string }[];
  reviewer_id: string | null; reviewer_name: string | null; review_note: string | null; review_requested_at: string | null;
  policy_requires_ack: boolean; has_unpublished_changes: boolean; my_vote: boolean | null;
  related: { id: string; title: string; collection_name: string | null }[]; can_edit: boolean; can_publish: boolean; viewing: 'published' | 'draft';
}
export interface KbArticleInput extends Partial<Audience> {
  title: string; body: RichDoc; summary?: string | null; collection_id?: string | null; tags?: string[]; is_template?: boolean;
  is_policy?: boolean; policy_requires_ack?: boolean; owner_id?: string;
}
export interface KbVersion { version: number; title: string; created_by_name: string; created_at: string; note: string | null; published: boolean; requires_reack: boolean }
export interface PolicyComplianceRow { user_id: string; display_name: string; location_names: string; acked_version: number | null; acked_at: string | null; current: boolean }

// ------------------------------------------------------------------ onboarding (#24)
export type OnboardingKind = 'onboarding' | 'offboarding';
export type OnboardingStepType = 'read_article' | 'ack_policy' | 'task' | 'skill' | 'meet' | 'video' | 'form' | 'checklist' | 'hr_details';
export const ONBOARDING_STEP_TYPES: { key: OnboardingStepType; label: string; auto: boolean }[] = [
  { key: 'read_article', label: 'Read an article', auto: true }, { key: 'ack_policy', label: 'Acknowledge a policy', auto: true },
  { key: 'task', label: 'Do a task', auto: false }, { key: 'skill', label: 'Get a skill', auto: true },
  { key: 'meet', label: 'Meet someone', auto: false }, { key: 'video', label: 'Watch a video', auto: false },
  { key: 'form', label: 'Fill in a form', auto: false }, { key: 'checklist', label: 'Complete a checklist', auto: true },
  // t368u: ticks itself once the person's HR record has everything required filled in.
  { key: 'hr_details', label: 'Fill in HR details', auto: true },
];
export type StepOwner = 'employee' | 'buddy' | 'manager';
export interface OnboardingStep {
  id: string; type: OnboardingStepType; title: string; details: string | null; ref_id: string | null; url: string | null;
  due_offset_days: number; owner: StepOwner;
}
export interface OnboardingTemplate {
  id: string; name: string; kind: OnboardingKind; description: string | null; position_ids: string[]; location_ids: string[];
  steps: OnboardingStep[]; active_count: number; archived_at: string | null; updated_at?: string;
}
export interface OnboardingStepState extends OnboardingStep {
  due_date: string; done_at: string | null; done_by_name: string | null; note: string | null; overdue: boolean; ref_label: string | null; can_tick: boolean;
  /** t612u: what ref_id points at, for the link: a Policies item (/policies/:id) or a knowledge base article (/kb/:id). */
  ref_kind?: 'policy' | 'article' | null;
}
export interface OnboardingAssignment {
  id: string; template_id: string; template_name: string; kind: OnboardingKind; user_id: string; user_name: string;
  buddy_id: string | null; buddy_name: string | null; manager_id: string | null; manager_name: string | null;
  start_date: string; status: 'active' | 'completed' | 'cancelled'; done: number; total: number; overdue: number; next_due: string | null;
  location_id: string | null; created_at: string; completed_at: string | null;
}
export interface OnboardingAssignmentDetail extends OnboardingAssignment { steps: OnboardingStepState[]; my_role: StepOwner | 'viewer' }

/** Due date for a step: start date + offset days (negative = before start, e.g. paperwork). */
export function stepDueDate(startDate: string, offsetDays: number): string {
  const d = new Date(`${startDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}
